import React, { useState } from 'react';

import { Attention, Close, Correct, Info } from '@travel/icons/ui';
import { cx } from '@travel/utils';

import stylesDefault from './alert.module.scss';
import stylesBalloon from './alertBalloon.module.scss';

import type { ReactNode } from 'react';

type Props = {
  /** Style name to control appearance of this component from parent component */
  className?: string;
  /** Style name for the title wrapper element */
  titleWrapperClassName?: string;
  /** Style name for the title element */
  titleClassName?: string;
  /** Style name for the description element */
  descriptionClassName?: string;
  /** Custom icon to display in the alert */
  icon?: ReactNode;
  /** Style name for the icon element */
  iconClassName?: string;
  /** Style name for the close button element */
  closeButtonClassName?: string;
  /** The type of Alert Component, may display different color, background color and icon */
  type?: 'success' | 'error' | 'info' | 'warning';
  /** Title. bold font-weight */
  title?: ReactNode;
  /** Description */
  description?: ReactNode;
  /** Flag to show close button */
  isClosable?: boolean;
  /** Flag for validation error */
  isValidation?: boolean;
  /** Style variation for the content  */
  variation?: 'default' | 'balloon';
  /** Callback function to be executed when the alert is closed */
  onClose?: () => void;
  /** Optional prop to override the default icon size for 'success' type. Does not affect existing usages if not provided. */
  successIconSize?: number;
  /** Optional prop to override the default icon size for 'error' type. Does not affect existing usages if not provided. */
  errorIconSize?: number;
  /** Optional prop to override the default icon size for 'info' type. Does not affect existing usages if not provided. */
  infoIconSize?: number;
  /** Optional prop to override the default icon size for 'warning' type. Does not affect existing usages if not provided. */
  warningIconSize?: number;
  /** Optional prop to override the default close icon size.  Does not affect existing usages if not provided. */
  closeIconSize?: number;
};

export const ERROR_ALERT_ID = 'error-alert';

export default function Alert(props: Props) {
  const {
    className,
    titleWrapperClassName,
    titleClassName,
    descriptionClassName,
    iconClassName,
    closeButtonClassName,
    title,
    description,
    type = 'info',
    isClosable = true,
    isValidation = false,
    variation = 'default',
    onClose,
    successIconSize,
    errorIconSize,
    infoIconSize,
    warningIconSize,
    closeIconSize,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState(true);

  const styles = variation === 'balloon' ? stylesBalloon : stylesDefault;

  let icon: null | ReactNode = null;
  let errorAlertId = '';
  let iconSize: number;
  switch (type) {
    case 'success':
      iconSize = successIconSize || 16;
      icon = <Correct size={iconSize} />;
      break;
    case 'error': {
      iconSize = errorIconSize || 16;
      icon = <Attention size={iconSize} />;
      errorAlertId = ERROR_ALERT_ID;
      break;
    }
    case 'info':
      iconSize = infoIconSize || 16;
      icon = <Info size={iconSize} />;
      break;
    case 'warning':
      iconSize = warningIconSize || 24;
      icon = <Info size={iconSize} />;
      break;
    default:
      iconSize = infoIconSize || 16;
      icon = <Info size={iconSize} />;
  }

  const onHandleOnClose = () => {
    setIsOpen(false);
  };

  const onTransitionEnd = () => {
    if (!isOpen) {
      onClose?.();
    }
  };

  return (
    <div
      className={cx(styles.alertWrapper, styles[type], isOpen || styles.isClose, className)}
      data-alert-id={errorAlertId}
      data-testid="alert-wrapper"
      {...rest}
      onTransitionEnd={onTransitionEnd}
    >
      <div className={cx(styles.titleWrapper, titleWrapperClassName)}>
        <div className={cx(styles.icon, iconClassName)}>{props.icon || icon}</div>
        <div className={cx(styles.title, titleClassName, isValidation && styles.errorMessage)}>
          {title}
        </div>
      </div>
      {isClosable && (
        <Close
          data-testId="close-icon"
          className={cx(styles.closeButton, closeButtonClassName)}
          onClick={onHandleOnClose}
          size={closeIconSize || 15}
        />
      )}
      {description && (
        <div className={cx(styles.description, descriptionClassName)}>{description}</div>
      )}
    </div>
  );
}
